import { workerUtilization } from "./roster.js";
import { STANDARD_DAY_HOURS } from "./capacity.js";
import { toDate, toIso } from "./schedule/calendar.js";

/* Weekly utilization, the number leadership actually asked for.
 *
 * Hours are kept per worker, per contract, per week. This turns those rows into
 * what a person reads on a utilization page: how full each person is each week,
 * their full-time-equivalent, and who crosses a full load. It leans on the
 * capacity math for the denominator, so a holiday week is measured against a
 * short week and a person full on it reads at a hundred percent rather than
 * looking slack.
 *
 * Pure throughout. Rows in, numbers out, so the grid, a rollup and a test all
 * see the same arithmetic.
 */

const DAY = 86400000;

/* The Monday of the week a date falls in, in UTC so it never drifts by a time
   zone. Every week in the app is named by its Monday, so an entry and a reading
   of the same week always line up. */
export function isoMonday(iso) {
  if (!iso) return null;
  const d = toDate(iso);
  const day = d.getUTCDay();               // 0 Sun .. 6 Sat
  const back = (day === 0 ? 6 : day - 1);  // days since Monday
  return toIso(new Date(d.getTime() - back * DAY));
}

export const weekEnd = (mondayIso) => toIso(new Date(toDate(mondayIso).getTime() + 6 * DAY));
export const addWeeks = (mondayIso, n) => toIso(new Date(toDate(mondayIso).getTime() + n * 7 * DAY));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/* A short, scannable column head: the month once, then the Monday's day. */
export function weekLabel(mondayIso) {
  const d = toDate(mondayIso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/* The run of weeks a grid shows, each as its Monday. */
export function weekColumns(startIso, count) {
  const first = isoMonday(startIso);
  return Array.from({ length: Math.max(0, count) }, (_, i) => addWeeks(first, i));
}

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/* Index the hours rows for quick lookup: total per worker and week, and the
   per-contract split within a week so a cell can show where the hours went. */
export function indexHours(rows = []) {
  const byWeek = new Map();        // `${worker}|${week}` -> total hours
  const byCell = new Map();        // `${worker}|${contract}|${week}` -> hours
  for (const r of rows) {
    const wk = r.week_start;
    const totalKey = `${r.worker_id}|${wk}`;
    const cellKey = `${r.worker_id}|${r.contract_id}|${wk}`;
    byWeek.set(totalKey, (byWeek.get(totalKey) || 0) + num(r.hours));
    byCell.set(cellKey, (byCell.get(cellKey) || 0) + num(r.hours));
  }
  return { byWeek, byCell };
}

const weeklyStandardOf = (worker) =>
  worker?.weekly_standard_hours ? Number(worker.weekly_standard_hours) : STANDARD_DAY_HOURS * 5;

/* One worker's reading for one week: hours worked, hours available with the
   holidays and their own short week folded in, the percentage, and the FTE. */
export function weekReading({ worker, weekStart, hours, holidays = [] }) {
  const { available, pct, band } = workerUtilization({
    worker, assignedHours: hours, start: weekStart, end: weekEnd(weekStart), holidays,
  });
  const weekly = weeklyStandardOf(worker);
  const fte = weekly > 0 ? Math.round((hours / weekly) * 100) / 100 : null;
  return { hours, available, pct, band, fte };
}

/* The grid: every active worker down the side, the chosen weeks across the top,
   each cell a reading. Exception first, so anyone who crosses a full load in
   any week of the window sorts to the top, then by their busiest week. */
export function utilizationGrid({ workers = [], hoursRows = [], weeks = [], holidays = [] }) {
  const { byWeek, byCell } = indexHours(hoursRows);
  const active = workers.filter((w) => !w.deleted_at && (w.status || "Active") !== "Inactive");

  const rows = active.map((worker) => {
    const cells = weeks.map((wk) => {
      const hours = byWeek.get(`${worker.id}|${wk}`) || 0;
      return { weekStart: wk, ...weekReading({ worker, weekStart: wk, hours, holidays }) };
    });
    const periodHours = cells.reduce((s, c) => s + c.hours, 0);
    const pcts = cells.map((c) => c.pct).filter((p) => p !== null);
    const peakPct = pcts.length ? Math.max(...pcts) : null;
    const avgPct = pcts.length ? Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length) : null;
    const weeksOver = cells.filter((c) => c.pct !== null && c.pct > 100).length;
    const weekly = weeklyStandardOf(worker);
    const fte = weeks.length && weekly > 0
      ? Math.round((periodHours / (weekly * weeks.length)) * 100) / 100 : null;
    return { worker, name: worker.name, cells, periodHours, peakPct, avgPct, weeksOver, fte };
  });

  rows.sort((a, b) =>
    (b.weeksOver > 0) - (a.weeksOver > 0)
    || (b.peakPct ?? -1) - (a.peakPct ?? -1)
    || a.name.localeCompare(b.name));

  return { rows, weeks, byCell };
}

/* The leader's summary above the grid: totals for the window, who crossed a
   full load, and the equivalent heads the hours add up to. */
export function utilizationSummary(grid) {
  const rows = grid.rows || [];
  const periodHours = rows.reduce((s, r) => s + r.periodHours, 0);
  const over = rows.filter((r) => r.weeksOver > 0);
  const anyHours = rows.filter((r) => r.periodHours > 0);
  const totalFte = rows.reduce((s, r) => s + (r.fte || 0), 0);
  return {
    people: anyHours.length,
    periodHours,
    overThisWindow: over.length,
    fte: Math.round(totalFte * 10) / 10,
  };
}

/* Group the grid by a worker attribute, the way the tracker pivots by manager
   or by base. Each group reports its heads, hours and equivalent FTE for the
   window, and how many of its people crossed a full load. */
export function utilizationRollup(grid, keyOf) {
  const groups = new Map();
  for (const r of grid.rows || []) {
    if (r.periodHours <= 0 && r.weeksOver === 0) continue;
    const key = keyOf(r.worker) || "Not set";
    if (!groups.has(key)) groups.set(key, { key, headcount: 0, periodHours: 0, fte: 0, over: 0 });
    const g = groups.get(key);
    g.headcount += 1;
    g.periodHours += r.periodHours;
    g.fte += r.fte || 0;
    g.over += r.weeksOver > 0 ? 1 : 0;
  }
  return [...groups.values()]
    .map((g) => ({ ...g, fte: Math.round(g.fte * 10) / 10 }))
    .sort((a, b) => b.periodHours - a.periodHours || a.key.localeCompare(b.key));
}
